import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';

import { NgxHttpClient } from '@app/shared/ngx-http-client';
import { TranslateService } from '@ngx-translate/core';
import { catchAndSurfaceError } from '@app/shared/utils/dg-error-helpers';
import { OpportunityPreferences } from '@app/profile-settings/profile-settings-api.model';
import { NotifierService } from '@app/shared/services/notifier.service';
import { TrackerService } from '@app/shared/services/tracker.service';

@Injectable({
  providedIn: 'root',
})
export class ProfileSettingsService {
  private _discardChanges = new Subject<void>();
  public readonly discardChanges = this._discardChanges.asObservable();

  private _changesSaved = new Subject<void>();
  public readonly changesSaved = this._changesSaved.asObservable();

  public discardAllChanges() {
    this._discardChanges.next();
  }
  public i18n = this.translate.instant([
    'UserSettingsSvc_DescAssignedLearning',
    'UserSettingsSvc_DescEndorsementRequest',
    'UserSettingsSvc_DescFollowers',
    'UserSettingsSvc_DescFollowingActivity',
    'UserSettingsSvc_DescGoals',
    'UserSettingsSvc_DescGroupPostAdded',
    'UserSettingsSvc_DescMention',
    'UserSettingsSvc_DescNewComment',
    'UserSettingsSvc_DescNewComment',
    'UserSettingsSvc_DescNewGoal',
    'UserSettingsSvc_DescRecommendations',
    'UserSettingsSvc_DescReplyComment',
    'UserSettingsSvc_DescSocialNotify',
    'UserSettingsSvc_EmailAddressesError',
    'UserSettingsSvc_JobRoleSaveFailed',
    'UserSettingsSvc_NotificationSettingsSaved',
    'UserSettingsSvc_NotificationSettingsSavedFailed',
    'UserSettingsSvc_NotificationTestSendFailed',
    'UserSettingsSvc_NotificationTestSent',
    'UserSettingsSvc_NotificationTypeDisplayText34',
    'UserSettingsSvc_NotificationTypeDisplayText35',
    'UserSettingsSvc_NotificationTypeDisplayText37',
    'UserSettingsSvc_NotificationTypeDisplayText61',
    'UserSettingsSvc_NotificationTypeDisplayText106',
    'UserSettingsSvc_PrivacySettingsSaved',
    'UserSettingsSvc_PrivacySettingsSaveFailed',
    'UserSettingsSvc_DescTodaysLearning',
    'UserSettingsSvc_DescRecommendation',
    'UserSettingsSvc_DescEndorsementRequest',
    'UsersSvc_UpdateOpportunityPreferencesError',
    'UserProfileSvc_ProblemAccessingCollection',
    'UsersSvc_GetOpportunityPreferencesError',
  ]);

  public notificationOptionDescriptions = {
    TodaysLearning: this.i18n.UserSettingsSvc_DescTodaysLearning,
    Followers: this.i18n.UserSettingsSvc_DescFollowers,
    FollowingActivity: this.i18n.UserSettingsSvc_DescFollowingActivity,
    Goals: this.i18n.UserSettingsSvc_DescGoals,
    Recommendations: this.i18n.UserSettingsSvc_DescRecommendations,
    GroupPostAdded: this.i18n.UserSettingsSvc_DescGroupPostAdded,
    NewGoal: this.i18n.UserSettingsSvc_DescNewGoal,
    NewComment: this.i18n.UserSettingsSvc_DescNewComment,
    ReplyComment: this.i18n.UserSettingsSvc_DescReplyComment,
    Mention: this.i18n.UserSettingsSvc_DescMention,
    AssignedLearning: this.i18n.UserSettingsSvc_DescAssignedLearning,
    SocialNotify: this.i18n.UserSettingsSvc_DescSocialNotify,
    EndorsementRequest: this.i18n.UserSettingsSvc_DescEndorsementRequest,
  };

  public notificationTypeDisplayTexts = {
    NotificationTypeDisplayText34:
      this.i18n.UserSettingsSvc_NotificationTypeDisplayText34,
    NotificationTypeDisplayText35:
      this.i18n.UserSettingsSvc_NotificationTypeDisplayText35,
    NotificationTypeDisplayText37:
      this.i18n.UserSettingsSvc_NotificationTypeDisplayText37,
    NotificationTypeDisplayText61:
      this.i18n.UserSettingsSvc_NotificationTypeDisplayText61,
    NotificationTypeDisplayText106:
      this.i18n.UserSettingsSvc_NotificationTypeDisplayText106,
  };
  constructor(
    private http: NgxHttpClient,
    private translate: TranslateService,
    private notifier: NotifierService,
    private tracker: TrackerService
  ) {}

  public settingsChangesSaved() {
    this._changesSaved.next();
  }

  public getProfileSettings(): any {
    return this.http
      .get('/organizations/GetSettingsForFrontend')
      .pipe(
        catchAndSurfaceError(
          this.i18n.UserProfileSvc_ProblemAccessingCollection
        )
      );
  }

  public getUserOpportunityPreferences(): Observable<OpportunityPreferences> {
    return this.http
      .get<OpportunityPreferences>('/user/opportunity-preferences')
      .pipe(
        catchAndSurfaceError(this.i18n.UsersSvc_GetOpportunityPreferencesError)
      );
  }

  /**
   * Update the opportunity type preferences for the user
   *
   * @returns {Observable} Observable<UserAPI.OpportunityPreferences>
   */
  public updateUserOpportunityPreferences = (
    isInterestedInOpportunities,
    opportunityTypes
  ) => {
    return this.http
      .put('/user/opportunity-preferences', {
        isInterestedInOpportunities,
        opportunityTypes,
      })
      .pipe(
        catchAndSurfaceError(
          this.i18n.UsersSvc_UpdateOpportunityPreferencesError
        )
      );
  };

  public canOptInTeam(orgId) {
    if (orgId) {
      return this.http.get<any>('/managers/CanUserOptIntoTeams', {
        params: {
          orgId,
        },
      });
    }
  }

  public getUserNotificationPreferences() {
    const notificationTypes = [
      'UserNotificationEmail',
      'DailySummaryEmail',
      'WeeklySummaryEmail',
      'TipEmail',
    ];

    // GetUserNotificationPreferences ep, requires the settings=type for each type, instead of settings=type,type
    // which is the what we get when supplying the array as params
    let qs = '';
    notificationTypes.forEach((type, index) => {
      qs += `settings=${type}`;
      if (index < notificationTypes.length - 1) {
        qs += '&';
      }
    });
    return this.http.get(`/user/GetUserNotificationPreferences?${qs}`);
  }

  public toggleNotificationPreference(
    notificationTypeId,
    isEnabled,
    suppressShowSuccess
  ) {
    return this.http
      .put('/user/ToggleUserNotification', {
        notificationTypeId: notificationTypeId,
        isEnabled: isEnabled,
      })
      .pipe(
        tap((response) => {
          let trackingCategories;

          if (!suppressShowSuccess) {
            this.notifier.showSuccess(
              this.i18n.UserSettingsSvc_NotificationSettingsSaved
            );
          }

          trackingCategories = {
            34: 'Immediate Notifications',
            35: 'Daily Digest Email',
            37: 'Weekly Digest Email',
            61: 'Email Tips',
          };

          this.tracker.trackEventData({
            action: 'Toggle',
            properties: {
              name: trackingCategories[notificationTypeId],
              toggled: isEnabled ? 'on' : 'off',
            },
          });

          return response;
        }),
        catchAndSurfaceError(
          this.i18n.UserSettingsSvc_NotificationSettingsSavedFailed
        )
      );
  }

  public updateNotificationOptions(
    notificationTypeId,
    options,
    optionChanged,
    isTurnedOff
  ) {
    return this.http
      .put('/user/UpdateUserNotificationOptions', {
        NotificationTypeId: notificationTypeId,
        Options: options,
      })
      .pipe(
        tap((response) => {
          let notificationIdCategories, category;

          this.notifier.showSuccess(
            this.i18n.UserSettingsSvc_NotificationSettingsSaved
          );

          // map for tracking
          notificationIdCategories = {
            34: {
              Recommendations: 'immediate new recommendations',
              GroupPostAdded: 'immediate new group post',
              NewComment: 'immediate new comments',
              Mention: 'immediate when mentioned',
              ReplyComment: 'immediate replies',
              AssignedLearning: 'immediate assigned learning',
              SocialNotify: 'immediate new friend',
              EndorsementRequest: 'immediate endorsement request',
            },
            35: {
              Recommendations: 'digest new recommendations',
              TodaysLearning: 'list of feed items',
              FollowingActivity: 'new activity',
              Followers: 'new followers',
              AssignedLearning: 'digest assigned learning',
            },
          };

          category =
            notificationIdCategories[notificationTypeId][optionChanged];
          this.tracker.trackEventData({
            action: 'Check',
            properties: {
              name: category,
              checked: isTurnedOff ? 'off' : 'on',
            },
          });
          return response;
        }),
        catchAndSurfaceError(
          this.i18n.UserSettingsSvc_NotificationSettingsSavedFailed
        )
      );
  }

  public sendTestEmail(notificationTypeName) {
    return this.http
      .get('/user/SendUserEmail', {
        params: { notificationType: notificationTypeName },
      })
      .pipe(
        tap((response) => {
          this.notifier.showSuccess(
            this.i18n.UserSettingsSvc_NotificationTestSent
          );
          return response;
        }),
        catchAndSurfaceError(
          this.i18n.UserSettingsSvc_NotificationTestSendFailed
        )
      );
  }

  public getEmailAddresses() {
    return this.http
      .get('/user/GetUserEmailAddresses')
      .pipe(
        catchAndSurfaceError(this.i18n.UserSettingsSvc_EmailAddressesError)
      );
  }

  public updateJobRole(jobRole) {
    return this.http
      .put('/user/UpdateJobRole', { JobRoleName: jobRole })
      .pipe(catchAndSurfaceError(this.i18n.UserSettingsSvc_JobRoleSaveFailed));
  }
}
